import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Tag, AlertTriangle, Heart, FileText, Activity, Sparkles, TrendingUp, Users, Clock } from '../lib/icons';
import { useTopicsList } from '../hooks/useTopics';
import { createUniqueSlug } from '../utils/slugUtils';

const TopicsLexicon: React.FC = () => {
  // useTopicsList() — Listen-Felder ohne content (~30 KB statt 3.7 MB).
  const { topics, loading, error } = useTopicsList();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'disease' | 'symptom' | 'consultation'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const filters = [
    { key: 'all', label: 'Alle Themen', icon: Sparkles, gradient: 'from-blue-500 to-purple-600' },
    { key: 'symptom', label: 'Symptome', icon: AlertTriangle, gradient: 'from-amber-500 to-orange-600' },
    { key: 'disease', label: 'Krankheiten', icon: Activity, gradient: 'from-red-500 to-pink-600' },
    { key: 'consultation', label: 'Beratung', icon: Heart, gradient: 'from-emerald-500 to-teal-600' }
  ];

  // Check if we're on the homepage (no search term, no filter, first page)
  const isHomepage = !searchTerm && selectedFilter === 'all' && currentPage === 1;
  const displayItemsPerPage = isHomepage ? 8 : itemsPerPage;

  // Filter topics based on search term and selected filter
  const filteredTopics = topics.filter(topic => {
    const matchesSearch = topic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (topic.description && topic.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = selectedFilter === 'all' || 
                         (topic as any).topic_type === selectedFilter;
    
    const isActive = topic.is_active;
    
    return matchesSearch && matchesFilter && isActive;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTopics.length / displayItemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = isHomepage ? displayItemsPerPage : startIndex + itemsPerPage;
  const paginatedTopics = isHomepage ? 
    filteredTopics.slice(0, displayItemsPerPage) : 
    filteredTopics.slice(startIndex, endIndex);

  // Reset page when filter or search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, searchTerm]);

  const getTopicGradient = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return 'from-red-500 to-pink-600';
      case 'symptom':
        return 'from-amber-500 to-orange-600';
      case 'consultation':
      default:
        return 'from-emerald-500 to-teal-600';
    }
  };

  const getTopicIcon = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return Activity;
      case 'symptom':
        return AlertTriangle;
      case 'consultation':
      default:
        return Heart;
    }
  };

  const getTopicTypeLabel = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return 'Krankheit';
      case 'symptom':
        return 'Symptom';
      case 'consultation':
      default:
        return 'Beratung';
    }
  };

  const handleTopicClick = (topic: any) => {
    const slug = createUniqueSlug(topic.name, topic.id);
    const topicType = (topic as any).topic_type || 'consultation';
    
    switch (topicType) {
      case 'disease':
        navigate(`/krankheiten/${slug}`);
        break;
      case 'symptom':
        navigate(`/symptome/${slug}`);
        break;
      case 'consultation':
      default:
        navigate(`/beratung/${slug}`);
        break;
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Gesundheitsthemen...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Fehler beim Laden der Themen
            </h3>
            <p className="text-gray-600">
              {error.includes('503') || error.includes('timeout') 
                ? 'Server temporär nicht verfügbar. Bitte versuchen Sie es später erneut.'
                : 'Ein unerwarteter Fehler ist aufgetreten.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-emerald-400/20 to-blue-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200/50 mb-6">
            <Sparkles className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-blue-600 font-medium text-sm">Vertrauenswürdige Gesundheitsinformationen</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Ihre Gesundheit verstehen
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Entdecken Sie aktuelle fundierte Informationen zu Symptomen, Krankheiten und Behandlungsmöglichkeiten – 
            <span className="text-blue-600 font-medium"> verständlich erklärt</span>
          </p>
        </div>

        {/* Enhanced Search */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200/50 p-2">
              <div className="relative">
                <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Wonach suchen Sie? z.B. Kopfschmerzen, Diabetes..."
                  className="w-full pl-16 pr-6 py-4 text-lg border-0 rounded-xl focus:ring-0 focus:outline-none placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modern Filter Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filters.map((filter) => {
            const IconComponent = filter.icon;
            const isActive = selectedFilter === filter.key;
            const count = filter.key === 'all' 
              ? topics.filter(t => t.is_active).length
              : topics.filter(t => t.is_active && (t as any).topic_type === filter.key).length;
            
            return (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key as any)}
                className={`group relative overflow-hidden rounded-2xl px-6 py-4 w-full sm:w-auto transition-all duration-300 transform hover:scale-105 ${
                  isActive 
                    ? 'shadow-xl' 
                    : 'shadow-lg hover:shadow-xl bg-white/80 backdrop-blur-sm border border-gray-200/50'
                }`}
              >
                {isActive && (
                  <div className={`absolute inset-0 bg-gradient-to-r ${filter.gradient} opacity-100`}></div>
                )}
                <div className="relative flex items-center justify-center sm:justify-start space-x-3">
                  <div className={`p-2 rounded-xl ${
                    isActive 
                      ? 'bg-white/20' 
                      : 'bg-gradient-to-r ' + filter.gradient + ' text-white'
                  }`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className={`font-semibold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                      {filter.label}
                    </div>
                    <div className={`text-sm ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                      {count} verfügbar
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Results Info with Animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-gray-200/50">
            <TrendingUp className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-gray-700 font-medium">
              {filteredTopics.length} {filteredTopics.length === 1 ? 'Thema gefunden' : 'Themen gefunden'}
              {searchTerm && (
                <span className="text-blue-600"> für "{searchTerm}"</span>
              )}
            </span>
          </div>
        </div>

        {/* Modern Topic Cards */}
        {paginatedTopics.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {paginatedTopics.map((topic, index) => {
              const topicType = (topic as any).topic_type || 'consultation';
              const gradientClass = getTopicGradient(topicType);
              const IconComponent = getTopicIcon(topicType);
              
              return (
                <div
                  key={topic.id}
                  className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-200/50 overflow-hidden transform hover:-translate-y-2"
                  onClick={() => handleTopicClick(topic)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Gradient Header */}
                  <div className={`h-24 bg-gradient-to-br ${gradientClass} relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-4 right-4">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                        {getTopicTypeLabel(topicType)}
                      </span>
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute -top-4 -left-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-white/10 rounded-full"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight group-hover:text-blue-600 transition-colors line-clamp-2">
                      {topic.name}
                    </h3>
                    
                    {topic.description && (
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                        {topic.description}
                      </p>
                    )}
                    
                    {/* Action Area */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>2-3 min Lesezeit</span>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <ChevronRight className="w-4 h-4 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileText className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Keine Themen gefunden
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {searchTerm 
                  ? `Für "${searchTerm}" wurden keine passenden Themen gefunden.`
                  : `Keine ${selectedFilter === 'all' ? '' : getTopicTypeLabel(selectedFilter)} verfügbar.`}
              </p>
              <div className="bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-2xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center justify-center">
                  <Search className="w-5 h-5 mr-2" />
                  Suchtipps:
                </h4>
                <ul className="text-blue-800 text-sm space-y-2">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                    Versuchen Sie allgemeinere Begriffe
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                    Prüfen Sie die Rechtschreibung
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                    Verwenden Sie andere Filter
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Zurück
            </button>
            
            <div className="flex items-center space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-12 h-12 rounded-xl font-semibold transition-all duration-300 ${
                      currentPage === pageNum
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-110'
                        : 'bg-white/80 backdrop-blur-sm text-gray-700 border border-gray-200/50 hover:bg-white hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-xl hover:bg-white hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium"
            >
              Weiter
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

      </div>
    </section>
  );
};

export default TopicsLexicon;